/**
 * White-background removal for catalog product images.
 *
 * Approach: flood fill from the image borders and remove only the connected
 * near-white/light background. White areas INSIDE the product (labels, packaging)
 * are never touched because they are not connected to the border. The cut edge then
 * gets an adaptive defringe pass: boundary pixels whose color is close to the
 * background (the light "matte" stroke that white items leave behind) are choked
 * hard, while high-contrast product edges only get gentle anti-aliasing. Finally the
 * remaining semi-transparent pixels are color de-contaminated (un-blended from the
 * background color) so no light halo shows on dark surfaces.
 *
 * "Surround product" mode (`outline` option): instead of choking the cut edge,
 * a band of the ORIGINAL background is kept around the product (sticker-style
 * white stroke). Protects items whose own edges are near-white — nothing of the
 * product can be eaten because the edge is never cut.
 *
 * Pure canvas/typed-array implementation — no dependencies, runs in the browser.
 */

const DEFAULT_MAX_DIMENSION = 3000

/**
 * Append a constant query param for pixel-access fetches.
 * A plain <img> load caches the response WITHOUT CORS headers; fetching the same
 * URL for canvas access would then fail. A separate cache key avoids that trap
 * while still letting the CDN/browser cache the CORS-enabled response.
 * Note: the name must not collide with reserved GCS API params (`cors`, `acl`, ...).
 */
function withCorsCacheKey(url) {
  return url + (url.includes('?') ? '&' : '?') + 'pxfetch=1'
}

/**
 * Fetch an image URL (CORS) and return its pixels as ImageData.
 * Downscales to maxDimension if the source is larger.
 * @param {string} url
 * @param {{ maxDimension?: number }} [options]
 * @returns {Promise<ImageData>}
 */
export async function loadImageDataFromUrl(url, options = {}) {
  const maxDimension = options.maxDimension || DEFAULT_MAX_DIMENSION

  const response = await fetch(withCorsCacheKey(url), { mode: 'cors' })
  if (!response.ok) {
    throw new Error(`Image fetch failed (${response.status})`)
  }
  const blob = await response.blob()

  // An object URL from a CORS-fetched blob is same-origin — the canvas stays clean
  const objectUrl = URL.createObjectURL(blob)
  try {
    const img = new Image()
    img.src = objectUrl
    await img.decode()

    const scale = Math.min(1, maxDimension / Math.max(img.naturalWidth, img.naturalHeight))
    const width = Math.max(1, Math.round(img.naturalWidth * scale))
    const height = Math.max(1, Math.round(img.naturalHeight * scale))

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    ctx.drawImage(img, 0, 0, width, height)
    return ctx.getImageData(0, 0, width, height)
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

/**
 * Estimate the background color from a 2px border ring.
 * Uses the per-channel median of the light border pixels; falls back to pure
 * white when the border is mostly dark (algorithm still behaves via tolerance).
 */
function sampleBackgroundColor(src, width, height) {
  const rs = []
  const gs = []
  const bs = []
  let ringCount = 0

  for (let y = 0; y < height; y++) {
    const edgeRow = y < 2 || y >= height - 2
    for (let x = 0; x < width; x++) {
      if (!edgeRow && x >= 2 && x < width - 2) continue
      ringCount++
      const o = (y * width + x) * 4
      const r = src[o]
      const g = src[o + 1]
      const b = src[o + 2]
      const luma = 0.299 * r + 0.587 * g + 0.114 * b
      if (luma >= 165 && Math.min(r, g, b) >= 130) {
        rs.push(r)
        gs.push(g)
        bs.push(b)
      }
    }
  }

  if (rs.length < ringCount * 0.2) {
    return { r: 255, g: 255, b: 255 }
  }

  const median = (arr) => {
    arr.sort((a, b) => a - b)
    return arr[arr.length >> 1]
  }
  return { r: median(rs), g: median(gs), b: median(bs) }
}

/**
 * Remove the edge-connected light background of an image.
 *
 * @param {ImageData} source - input pixels (not mutated)
 * @param {{ tolerance?: number, outline?: number }} [options] - tolerance 0..100 (default 30);
 *   higher removes off-white shades and soft shadows more aggressively.
 *   `outline` > 0 switches to "surround product" mode: keeps a stroke of the
 *   original background that wide (px) around the product instead of cutting
 *   flush (skips the matte choke / de-contamination — not needed there)
 * @returns {{ imageData: ImageData, removedRatio: number, bgColor: {r:number,g:number,b:number} }}
 */
export function removeWhiteBackground(source, options = {}) {
  const rawTolerance = Number(options.tolerance)
  const tolerance = Math.min(100, Math.max(0, isNaN(rawTolerance) ? 30 : rawTolerance))
  const rawOutline = Number(options.outline)
  const outline = Math.max(0, isNaN(rawOutline) ? 0 : Math.round(rawOutline))

  const { width, height } = source
  const src = source.data
  const totalPixels = width * height

  const bg = sampleBackgroundColor(src, width, height)

  // Hard cutoff (fully transparent) and soft fringe band, in RGB Euclidean distance
  const hard = 8 + tolerance * 1.15
  const band = Math.max(14, hard * 0.4)
  const reach = hard + band
  const hardSq = hard * hard
  const reachSq = reach * reach

  const distSqAt = (idx) => {
    const o = idx * 4
    const dr = src[o] - bg.r
    const dg = src[o + 1] - bg.g
    const db = src[o + 2] - bg.b
    return dr * dr + dg * dg + db * db
  }

  // ---- flood fill from the borders: state 1 = connected background ----
  const state = new Uint8Array(totalPixels) // 0 fg-unvisited, 1 background, 2 fg-visited
  const stack = new Int32Array(totalPixels)
  let stackSize = 0

  const trySeed = (idx) => {
    if (state[idx] === 0 && distSqAt(idx) <= reachSq) {
      state[idx] = 1
      stack[stackSize++] = idx
    }
  }
  for (let x = 0; x < width; x++) {
    trySeed(x)
    trySeed((height - 1) * width + x)
  }
  for (let y = 1; y < height - 1; y++) {
    trySeed(y * width)
    trySeed(y * width + width - 1)
  }

  while (stackSize > 0) {
    const idx = stack[--stackSize]
    const x = idx % width
    let n
    if (x > 0) {
      n = idx - 1
      if (state[n] === 0 && distSqAt(n) <= reachSq) {
        state[n] = 1
        stack[stackSize++] = n
      }
    }
    if (x < width - 1) {
      n = idx + 1
      if (state[n] === 0 && distSqAt(n) <= reachSq) {
        state[n] = 1
        stack[stackSize++] = n
      }
    }
    if (idx >= width) {
      n = idx - width
      if (state[n] === 0 && distSqAt(n) <= reachSq) {
        state[n] = 1
        stack[stackSize++] = n
      }
    }
    if (idx < totalPixels - width) {
      n = idx + width
      if (state[n] === 0 && distSqAt(n) <= reachSq) {
        state[n] = 1
        stack[stackSize++] = n
      }
    }
  }

  // ---- alpha map + marks (1 = newly removed, 2 = ring1, 3 = ring2) ----
  const alpha = new Float32Array(totalPixels)
  const marks = new Uint8Array(totalPixels)
  for (let idx = 0; idx < totalPixels; idx++) {
    const srcA = src[idx * 4 + 3]
    if (state[idx] === 1) {
      const dSq = distSqAt(idx)
      if (dSq <= hardSq) {
        alpha[idx] = 0
        if (srcA > 0) marks[idx] = 1
      } else {
        const d = Math.sqrt(dSq)
        const bandAlpha = Math.max(1, Math.min(254, Math.round(((d - hard) / band) * 255)))
        alpha[idx] = Math.min(srcA, bandAlpha)
      }
    } else {
      alpha[idx] = srcA
    }
  }

  // "Near-background" color class: pixels this close to the bg color are
  // considered background residue when they hug or float in the removed area
  const nearBgSq = reach * 1.35 * (reach * 1.35)

  // ---- despeckle + residue islands ----
  // Remove foreground islands floating in the removed area when they are either
  // tiny (dust) or made ENTIRELY of near-background colors of any thin shape
  // (the detached light-gray arcs white items leave behind). A real detached
  // product part always contains pixels that differ from the background.
  const speckMax = totalPixels < 400 ? 0 : Math.max(24, Math.round(totalPixels * 0.0001))
  const residueMax = Math.round(totalPixels * 0.02)
  const island = new Int32Array(residueMax + 1)
  for (let start = 0; start < totalPixels; start++) {
    if (state[start] !== 0 || alpha[start] === 0) continue
    let size = 0
    let allNearBg = true
    state[start] = 2
    stack[0] = start
    stackSize = 1
    while (stackSize > 0) {
      const idx = stack[--stackSize]
      if (size < island.length) island[size] = idx
      size++
      if (allNearBg && distSqAt(idx) >= nearBgSq) allNearBg = false
      const x = idx % width
      let n
      if (x > 0) {
        n = idx - 1
        if (state[n] === 0 && alpha[n] > 0) {
          state[n] = 2
          stack[stackSize++] = n
        }
      }
      if (x < width - 1) {
        n = idx + 1
        if (state[n] === 0 && alpha[n] > 0) {
          state[n] = 2
          stack[stackSize++] = n
        }
      }
      if (idx >= width) {
        n = idx - width
        if (state[n] === 0 && alpha[n] > 0) {
          state[n] = 2
          stack[stackSize++] = n
        }
      }
      if (idx < totalPixels - width) {
        n = idx + width
        if (state[n] === 0 && alpha[n] > 0) {
          state[n] = 2
          stack[stackSize++] = n
        }
      }
    }
    const isDust = size <= speckMax
    const isResidue = allNearBg && size <= residueMax
    if (isDust || isResidue) {
      for (let i = 0; i < size; i++) {
        alpha[island[i]] = 0
        marks[island[i]] = 1
      }
    }
  }

  // ---- "surround product" mode: keep an original-background stroke ----
  // The product edge sits on the kept background band, so no defringe or
  // de-contamination is needed — compose and return here.
  if (outline > 0) {
    const fgMask = new Uint8Array(totalPixels)
    for (let idx = 0; idx < totalPixels; idx++) {
      if (state[idx] !== 1 && alpha[idx] > 0) fgMask[idx] = 1
    }
    const composed = composeStickerOutline(source, fgMask, outline)
    return { ...composed, bgColor: bg }
  }

  // ---- adaptive defringe (matte choke), 4px deep from the removed area ----
  // Multi-source BFS out of the removed region. Near-background pixels (the
  // light "stroke" that white items leave behind) are crushed hard across all
  // four rings; high-contrast product edges only get gentle anti-aliasing.
  const factorsNearBg = [0.06, 0.1, 0.2, 0.45]
  const factorsEdge = [0.55, 0.75, 0.9, 1]
  let frontier = new Int32Array(totalPixels)
  let next = new Int32Array(totalPixels)
  let frontierSize = 0
  for (let idx = 0; idx < totalPixels; idx++) {
    if (marks[idx] === 1) frontier[frontierSize++] = idx
  }
  for (let depth = 0; depth < 4 && frontierSize > 0; depth++) {
    const fNear = factorsNearBg[depth]
    const fEdge = factorsEdge[depth]
    let nextSize = 0
    for (let i = 0; i < frontierSize; i++) {
      const idx = frontier[i]
      const x = idx % width
      let n
      if (x > 0) {
        n = idx - 1
        if (alpha[n] > 0 && marks[n] === 0) {
          marks[n] = 2 + depth
          alpha[n] *= distSqAt(n) < nearBgSq ? fNear : fEdge
          next[nextSize++] = n
        }
      }
      if (x < width - 1) {
        n = idx + 1
        if (alpha[n] > 0 && marks[n] === 0) {
          marks[n] = 2 + depth
          alpha[n] *= distSqAt(n) < nearBgSq ? fNear : fEdge
          next[nextSize++] = n
        }
      }
      if (idx >= width) {
        n = idx - width
        if (alpha[n] > 0 && marks[n] === 0) {
          marks[n] = 2 + depth
          alpha[n] *= distSqAt(n) < nearBgSq ? fNear : fEdge
          next[nextSize++] = n
        }
      }
      if (idx < totalPixels - width) {
        n = idx + width
        if (alpha[n] > 0 && marks[n] === 0) {
          marks[n] = 2 + depth
          alpha[n] *= distSqAt(n) < nearBgSq ? fNear : fEdge
          next[nextSize++] = n
        }
      }
    }
    const swap = frontier
    frontier = next
    next = swap
    frontierSize = nextSize
  }

  // ---- final compose: quantize alpha, de-contaminate blended pixels ----
  const out = new Uint8ClampedArray(src.length)
  let newlyCleared = 0
  for (let idx = 0; idx < totalPixels; idx++) {
    const o = idx * 4
    const srcA = src[o + 3]
    let a = Math.round(alpha[idx])
    if (a < 6) a = 0
    if (a === 0) {
      // fully background: leave RGB at 0 so no hidden data ships in the PNG
      if (srcA > 0) newlyCleared++
      continue
    }
    if (a < srcA) {
      // un-blend: pixel was (fg * a + bg * (1-a)); recover fg to avoid a light halo
      const af = a / 255
      out[o] = (src[o] - (1 - af) * bg.r) / af
      out[o + 1] = (src[o + 1] - (1 - af) * bg.g) / af
      out[o + 2] = (src[o + 2] - (1 - af) * bg.b) / af
    } else {
      out[o] = src[o]
      out[o + 1] = src[o + 1]
      out[o + 2] = src[o + 2]
    }
    out[o + 3] = a
  }

  return {
    imageData: new ImageData(out, width, height),
    removedRatio: totalPixels > 0 ? newlyCleared / totalPixels : 0,
    bgColor: bg,
  }
}

/**
 * Separable box blur (horizontal pass) with clamp-to-edge, running-sum O(n).
 */
function boxBlurRows(src, dst, width, height, radius) {
  const win = radius * 2 + 1
  for (let y = 0; y < height; y++) {
    const row = y * width
    let sum = src[row] * (radius + 1)
    for (let x = 1; x <= radius; x++) sum += src[row + Math.min(x, width - 1)]
    for (let x = 0; x < width; x++) {
      dst[row + x] = sum / win
      sum += src[row + Math.min(x + radius + 1, width - 1)] - src[row + Math.max(x - radius, 0)]
    }
  }
}

/**
 * Separable box blur (vertical pass) with clamp-to-edge, row-major friendly.
 */
function boxBlurCols(src, dst, width, height, radius) {
  const win = radius * 2 + 1
  const sums = new Float32Array(width)
  for (let x = 0; x < width; x++) sums[x] = src[x] * (radius + 1)
  for (let y = 1; y <= radius; y++) {
    const row = Math.min(y, height - 1) * width
    for (let x = 0; x < width; x++) sums[x] += src[row + x]
  }
  for (let y = 0; y < height; y++) {
    const out = y * width
    const addRow = Math.min(y + radius + 1, height - 1) * width
    const subRow = Math.max(y - radius, 0) * width
    for (let x = 0; x < width; x++) {
      dst[out + x] = sums[x] / win
      sums[x] += src[addRow + x] - src[subRow + x]
    }
  }
}

/**
 * "Surround product" compose: keep the product AND every background pixel
 * within `outline` px of it at their ORIGINAL colors (a sticker-style white
 * band), clear everything further out. Uses a chamfer 3-4 distance transform
 * from the given foreground; the distance field is Gaussian-smoothed before
 * thresholding so the stroke's outer contour is round and calm — mask bumps,
 * ragged cut edges and chamfer facets don't print through to the outline.
 *
 * Engine-agnostic: `foregroundMask` can come from the classic flood-fill or
 * from an AI matte (see aiBgRemoval.js).
 *
 * @param {ImageData} source - original pixels (not mutated)
 * @param {Uint8Array} foregroundMask - width*height, nonzero = product pixel
 * @param {number} outline - stroke width in px
 * @returns {{ imageData: ImageData, removedRatio: number }}
 */
export function composeStickerOutline(source, foregroundMask, outline) {
  const { width, height } = source
  const src = source.data
  const totalPixels = width * height

  // Distance to the nearest foreground pixel, in chamfer units (3 ≈ 1px)
  const INF = 0x3fffffff
  const dist = new Int32Array(totalPixels)
  for (let idx = 0; idx < totalPixels; idx++) {
    dist[idx] = foregroundMask[idx] ? 0 : INF
  }
  for (let y = 0; y < height; y++) {
    const row = y * width
    for (let x = 0; x < width; x++) {
      const idx = row + x
      let d = dist[idx]
      if (d === 0) continue
      if (x > 0 && dist[idx - 1] + 3 < d) d = dist[idx - 1] + 3
      if (y > 0) {
        const up = idx - width
        if (dist[up] + 3 < d) d = dist[up] + 3
        if (x > 0 && dist[up - 1] + 4 < d) d = dist[up - 1] + 4
        if (x < width - 1 && dist[up + 1] + 4 < d) d = dist[up + 1] + 4
      }
      dist[idx] = d
    }
  }
  for (let y = height - 1; y >= 0; y--) {
    const row = y * width
    for (let x = width - 1; x >= 0; x--) {
      const idx = row + x
      let d = dist[idx]
      if (d === 0) continue
      if (x < width - 1 && dist[idx + 1] + 3 < d) d = dist[idx + 1] + 3
      if (y < height - 1) {
        const down = idx + width
        if (dist[down] + 3 < d) d = dist[down] + 3
        if (x < width - 1 && dist[down + 1] + 4 < d) d = dist[down + 1] + 4
        if (x > 0 && dist[down - 1] + 4 < d) d = dist[down - 1] + 4
      }
      dist[idx] = d
    }
  }

  // ---- smooth the distance field so the outline contour is round ----
  // 3× separable box blur ≈ Gaussian (σ ≈ blurRadius px). Taking the stroke
  // as a level-set of the SMOOTHED field rounds out every bump/jag the raw
  // mask would otherwise print onto the outer edge. Far values are clamped
  // first so the running sums stay precise.
  const threshold = outline * 3
  const blurRadius = Math.max(3, Math.round(outline * 0.8))
  const fieldCap = threshold + (blurRadius * 3 + 12) * 3
  const fieldA = new Float32Array(totalPixels)
  for (let idx = 0; idx < totalPixels; idx++) {
    fieldA[idx] = dist[idx] > fieldCap ? fieldCap : dist[idx]
  }
  const fieldB = new Float32Array(totalPixels)
  for (let pass = 0; pass < 3; pass++) {
    boxBlurRows(fieldA, fieldB, width, height, blurRadius)
    boxBlurCols(fieldB, fieldA, width, height, blurRadius)
  }

  const out = new Uint8ClampedArray(src.length)
  let newlyCleared = 0
  for (let idx = 0; idx < totalPixels; idx++) {
    const o = idx * 4
    const srcA = src[o + 3]
    let a
    if (dist[idx] === 0) {
      // Product pixel: kept fully intact at its original alpha
      a = srcA
    } else {
      // Stroke band: full original pixel, anti-aliased along the smoothed rim
      const coverage = Math.min(1, Math.max(0, (threshold + 4.5 - fieldA[idx]) / 9))
      a = Math.round(coverage * srcA)
    }
    if (a < 6) a = 0
    if (a === 0) {
      if (srcA > 0) newlyCleared++
      continue
    }
    out[o] = src[o]
    out[o + 1] = src[o + 1]
    out[o + 2] = src[o + 2]
    out[o + 3] = a
  }

  return {
    imageData: new ImageData(out, width, height),
    removedRatio: totalPixels > 0 ? newlyCleared / totalPixels : 0,
  }
}

/**
 * Crop an image to its visible (non-transparent) content plus padding.
 * Padding is clamped to the original bounds — the image is never enlarged.
 * Returns the input ImageData unchanged when there is nothing to crop.
 * @param {ImageData} imageData
 * @param {{ padding?: number, alphaThreshold?: number }} [options]
 * @returns {ImageData}
 */
export function cropToContent(imageData, options = {}) {
  const padding = options.padding ?? 40
  const alphaThreshold = options.alphaThreshold ?? 8
  const { width, height, data } = imageData

  let minX = width
  let minY = height
  let maxX = -1
  let maxY = -1
  for (let y = 0; y < height; y++) {
    const rowStart = y * width
    for (let x = 0; x < width; x++) {
      if (data[(rowStart + x) * 4 + 3] >= alphaThreshold) {
        if (x < minX) minX = x
        if (x > maxX) maxX = x
        if (y < minY) minY = y
        if (y > maxY) maxY = y
      }
    }
  }

  // Fully transparent image — nothing to anchor a crop on
  if (maxX < 0) return imageData

  const x0 = Math.max(0, minX - padding)
  const y0 = Math.max(0, minY - padding)
  const x1 = Math.min(width - 1, maxX + padding)
  const y1 = Math.min(height - 1, maxY + padding)
  const newWidth = x1 - x0 + 1
  const newHeight = y1 - y0 + 1
  if (newWidth === width && newHeight === height) return imageData

  const outData = new Uint8ClampedArray(newWidth * newHeight * 4)
  for (let y = 0; y < newHeight; y++) {
    const srcStart = ((y0 + y) * width + x0) * 4
    outData.set(data.subarray(srcStart, srcStart + newWidth * 4), y * newWidth * 4)
  }
  return new ImageData(outData, newWidth, newHeight)
}

/**
 * Encode ImageData as a PNG Blob (keeps transparency).
 * @param {ImageData} imageData
 * @returns {Promise<Blob>}
 */
export function imageDataToPngBlob(imageData) {
  const canvas = document.createElement('canvas')
  canvas.width = imageData.width
  canvas.height = imageData.height
  canvas.getContext('2d').putImageData(imageData, 0, 0)
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('PNG encoding failed'))),
      'image/png'
    )
  })
}

/**
 * Read a Blob as a base64 data URL (for sending to Cloud Functions).
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
export function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(new Error('Failed to read image data'))
    reader.readAsDataURL(blob)
  })
}
